import apiService from './api';
import {
  ROIDashboardData,
  InteractionMetricsData,
  OrganizationalStructure,
  FinancialEventsResponse,
  AIPrompt,
  UpdatePromptRequest,
  FinancialEventType,
  FinancialEventStatus,
  AIPromptType,
  InteractiveMenu,
  CreateInteractiveMenuRequest,
  UpdateInteractiveMenuRequest,
  SendInteractiveMenuRequest,
  InteractionLifecycle,
  InteractionMessage,
} from '../types/bi';

class BIApi {
  /**
   * Busca dados do dashboard de ROI
   */
  async getRoiDashboard(
    clientId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<ROIDashboardData> {
    try {
      console.log('🔍 [biApi] getRoiDashboard chamado com:', {
        clientId,
        startDate,
        endDate,
      });

      const params = new URLSearchParams();
      if (clientId) params.append('client_id', clientId.toString());
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const url = `/bi/roi-dashboard?${params.toString()}`;
      console.log('🌐 [biApi] Fazendo requisição para:', url);

      const response = await apiService.get(url);
      
      console.log('📦 [biApi] Resposta recebida:', {
        type: typeof response,
        isObject: typeof response === 'object',
        hasPeriod: response?.period !== undefined,
        hasByEventType: Array.isArray(response?.byEventType),
        preview: JSON.stringify(response).substring(0, 300),
      });

      if (!response || typeof response !== 'object') {
        console.error('❌ [biApi] Resposta inválida:', response);
        throw new Error('Resposta da API não é um objeto válido');
      }

      console.log(`✅ [biApi] Retornando dados de ROI`);
      return response as ROIDashboardData;
    } catch (error) {
      console.error('❌ [biApi] Erro ao buscar ROI dashboard:', error);
      console.error('❌ [biApi] Stack:', error instanceof Error ? error.stack : 'N/A');
      throw error;
    }
  }

  /**
   * Busca departamentos únicos da tabela interaction_lifecycle
   */
  async getInteractionDepartments(
    clientId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<string[]> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await apiService.get(
      `/bi/interaction-departments?${params.toString()}`
    );
    return response as string[];
  }

  /**
   * Busca agentes únicos da tabela interaction_lifecycle
   */
  async getInteractionAgents(
    clientId?: number,
    startDate?: string,
    endDate?: string,
    department?: string
  ): Promise<Array<{ agent_id: string; department: string }>> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (department) params.append('department', department);

    const response = await apiService.get(
      `/bi/interaction-agents?${params.toString()}`
    );
    return response as Array<{ agent_id: string; department: string }>;
  }

  /**
   * Busca detalhes completos de uma interação (incluindo mensagens)
   */
  async getInteractionDetails(
    protocol: string,
    clientId?: number
  ): Promise<{ interaction: InteractionLifecycle; messages: InteractionMessage[] }> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());

    const response = await apiService.get(
      `/bi/interactions/${protocol}?${params.toString()}`
    );
    return response as { interaction: InteractionLifecycle; messages: InteractionMessage[] };
  }

  /**
   * Busca interações com filtros avançados
   */
  async getInteractions(
    clientId?: number,
    protocol?: string,
    customerName?: string,
    customerPhone?: string,
    departments?: string[],
    connections?: string[],
    typeContact?: string,
    agents?: string[],
    slaStatus?: string,
    startDateFrom?: string,
    startDateTo?: string,
    endDateFrom?: string,
    endDateTo?: string
  ): Promise<InteractionLifecycle[]> {
    try {
      console.log('🔍 [biApi] getInteractions chamado com:', {
        clientId,
        protocol,
        customerName,
        customerPhone,
        departments,
        connections,
        typeContact,
        agents,
        slaStatus,
        startDateFrom,
        startDateTo,
        endDateFrom,
        endDateTo,
      });

      const params = new URLSearchParams();
      if (clientId) params.append('client_id', clientId.toString());
      if (protocol) params.append('protocol', protocol);
      if (customerName) params.append('customer_name', customerName);
      if (customerPhone) params.append('customer_phone', customerPhone);
      if (departments && departments.length > 0) params.append('departments', departments.join(','));
      if (connections && connections.length > 0) params.append('connections', connections.join(','));
      if (typeContact) params.append('type_contact', typeContact);
      if (agents && agents.length > 0) params.append('agents', agents.join(','));
      if (slaStatus) params.append('sla_status', slaStatus);
      if (startDateFrom) params.append('start_date_from', startDateFrom);
      if (startDateTo) params.append('start_date_to', startDateTo);
      if (endDateFrom) params.append('end_date_from', endDateFrom);
      if (endDateTo) params.append('end_date_to', endDateTo);

      const url = `/bi/interactions?${params.toString()}`;
      console.log('🌐 [biApi] Fazendo requisição para:', url);

      const response = await apiService.get(url);
      
      console.log('📦 [biApi] Resposta recebida:', {
        type: typeof response,
        isArray: Array.isArray(response),
        length: Array.isArray(response) ? response.length : 'N/A',
        preview: Array.isArray(response) && response.length > 0 ? response[0] : response,
      });

      if (!Array.isArray(response)) {
        console.error('❌ [biApi] Resposta não é um array:', response);
        throw new Error('Resposta da API não é um array de interações');
      }

      console.log(`✅ [biApi] Retornando ${response.length} interações`);
      return response as InteractionLifecycle[];
    } catch (error) {
      console.error('❌ [biApi] Erro ao buscar interações:', error);
      console.error('❌ [biApi] Stack:', error instanceof Error ? error.stack : 'N/A');
      throw error;
    }
  }

  /**
   * Busca conexões únicas da tabela interaction_lifecycle
   */
  async getInteractionConnections(
    clientId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<string[]> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await apiService.get(
      `/bi/interaction-connections?${params.toString()}`
    );
    return response as string[];
  }

  /**
   * Busca métricas de interações
   */
  async getInteractionMetrics(
    clientId?: number,
    startDate?: string,
    endDate?: string,
    department?: string,
    agentId?: string,
    connection?: string,
    typeContact?: string
  ): Promise<InteractionMetricsData> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (department) params.append('department', department);
    if (agentId) params.append('agent_id', agentId);
    if (connection) params.append('connection', connection);
    if (typeContact) params.append('type_contact', typeContact);

    const response = await apiService.get(
      `/bi/interaction-metrics?${params.toString()}`
    );
    return response as InteractionMetricsData;
  }

  /**
   * Busca estrutura organizacional
   */
  async getOrganizationalStructure(
    clientId?: number
  ): Promise<OrganizationalStructure[]> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());

    const response = await apiService.get(
      `/bi/organizational-structure?${params.toString()}`
    );
    return response as OrganizationalStructure[];
  }

  /**
   * Busca departamentos únicos
   */
  async getDepartments(clientId?: number): Promise<Array<{
    department_id: number;
    department_name: string;
    agents_count: number;
  }>> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());

    const response = await apiService.get(
      `/bi/departments?${params.toString()}`
    );
    return response as Array<{
      department_id: number;
      department_name: string;
      agents_count: number;
    }>;
  }

  /**
   * Cria um novo departamento
   */
  async createDepartment(data: {
    client_id?: number;
    department_name: string;
    department_id: number;
  }): Promise<OrganizationalStructure> {
    const response = await apiService.post('/bi/departments', data);
    return response as OrganizationalStructure;
  }

  /**
   * Atualiza um departamento
   */
  async updateDepartment(
    id: number,
    data: {
      client_id?: number;
      department_name: string;
    }
  ): Promise<OrganizationalStructure> {
    const response = await apiService.put(`/bi/departments/${id}`, data);
    return response as OrganizationalStructure;
  }

  /**
   * Deleta um departamento
   */
  async deleteDepartment(
    departmentId: number,
    clientId?: number
  ): Promise<void> {
    const params = new URLSearchParams();
    params.append('department_id', departmentId.toString());
    if (clientId) params.append('client_id', clientId.toString());
    // Usar um ID dummy na rota, pois o department_id real vem no query
    await apiService.delete(
      `/bi/departments/delete?${params.toString()}`
    );
  }

  /**
   * Busca eventos financeiros com filtros
   */
  async getFinancialEvents(params?: {
    clientId?: number;
    interactionId?: string;
    eventType?: FinancialEventType;
    status?: FinancialEventStatus;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<FinancialEventsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.clientId)
      queryParams.append('client_id', params.clientId.toString());
    if (params?.interactionId)
      queryParams.append('interaction_id', params.interactionId);
    if (params?.eventType)
      queryParams.append('event_type', params.eventType);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startDate)
      queryParams.append('start_date', params.startDate);
    if (params?.endDate) queryParams.append('end_date', params.endDate);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiService.get(
      `/bi/financial-events?${queryParams.toString()}`
    );
    return response as FinancialEventsResponse;
  }

  /**
   * Lista prompts de IA configurados
   */
  async getPrompts(params?: {
    clientId?: number;
    promptType?: AIPromptType;
    isActive?: boolean;
  }): Promise<AIPrompt[]> {
    const queryParams = new URLSearchParams();
    if (params?.clientId)
      queryParams.append('client_id', params.clientId.toString());
    if (params?.promptType)
      queryParams.append('prompt_type', params.promptType);
    if (params?.isActive !== undefined)
      queryParams.append('is_active', params.isActive.toString());

    const response = await apiService.get(
      `/bi/prompts?${queryParams.toString()}`
    );
    return response as AIPrompt[];
  }

  /**
   * Atualiza um prompt de IA
   */
  async updatePrompt(
    id: number,
    data: UpdatePromptRequest
  ): Promise<AIPrompt> {
    const response = await apiService.put(`/bi/prompts/${id}`, data);
    return response as AIPrompt;
  }

  /**
   * Cria um novo prompt de IA
   */
  async createPrompt(data: {
    client_id?: number | null;
    prompt_type: AIPromptType;
    name: string;
    description?: string;
    system_prompt: string;
    user_prompt_template: string;
    output_schema?: Record<string, any>;
    is_active?: boolean;
  }): Promise<AIPrompt> {
    const response = await apiService.post('/bi/prompts', data);
    return response as AIPrompt;
  }

  /**
   * Deleta um prompt de IA
   */
  async deletePrompt(id: number): Promise<void> {
    await apiService.delete(`/bi/prompts/${id}`);
  }

  /**
   * Cria nova estrutura organizacional (atendente vinculado a departamento)
   */
  async createOrganizationalStructure(data: {
    client_id?: number;
    agent_id: number;
    agent_name: string;
    department_id: number;
    contact_phone?: string;
  }): Promise<OrganizationalStructure> {
    const response = await apiService.post(
      '/bi/organizational-structure',
      data
    );
    return response as OrganizationalStructure;
  }

  /**
   * Atualiza estrutura organizacional
   */
  async updateOrganizationalStructure(
    id: number,
    data: {
      client_id?: number;
      agent_name?: string;
      department_name?: string;
      department_id?: number;
      contact_phone?: string;
    }
  ): Promise<OrganizationalStructure> {
    const response = await apiService.put(
      `/bi/organizational-structure/${id}`,
      data
    );
    return response as OrganizationalStructure;
  }

  /**
   * Deleta estrutura organizacional
   */
  async deleteOrganizationalStructure(
    id: number,
    clientId?: number
  ): Promise<void> {
    const params = new URLSearchParams();
    if (clientId) params.append('client_id', clientId.toString());
    await apiService.delete(
      `/bi/organizational-structure/${id}?${params.toString()}`
    );
  }

  /**
   * Lista menus interativos do cliente
   */
  async getInteractiveMenus(params?: {
    clientId?: number;
    isActive?: boolean;
  }): Promise<InteractiveMenu[]> {
    const queryParams = new URLSearchParams();
    if (params?.clientId)
      queryParams.append('client_id', params.clientId.toString());
    if (params?.isActive !== undefined)
      queryParams.append('is_active', params.isActive.toString());

    const response = await apiService.get(
      `/bi/interactive-menus?${queryParams.toString()}`
    );
    return response as InteractiveMenu[];
  }

  /**
   * Busca um menu interativo específico
   */
  async getInteractiveMenu(id: number): Promise<InteractiveMenu> {
    const response = await apiService.get(`/bi/interactive-menus/${id}`);
    return response as InteractiveMenu;
  }

  /**
   * Cria um novo menu interativo
   */
  async createInteractiveMenu(
    data: CreateInteractiveMenuRequest
  ): Promise<InteractiveMenu> {
    const response = await apiService.post('/bi/interactive-menus', data);
    return response as InteractiveMenu;
  }

  /**
   * Atualiza um menu interativo
   */
  async updateInteractiveMenu(
    id: number,
    data: UpdateInteractiveMenuRequest
  ): Promise<InteractiveMenu> {
    const response = await apiService.put(`/bi/interactive-menus/${id}`, data);
    return response as InteractiveMenu;
  }

  /**
   * Deleta um menu interativo
   */
  async deleteInteractiveMenu(id: number): Promise<void> {
    await apiService.delete(`/bi/interactive-menus/${id}`);
  }

  /**
   * Envia um menu interativo para um número
   */
  async sendInteractiveMenu(
    menuId: number,
    phoneNumber: string,
    connectionId: number
  ): Promise<any> {
    const response = await apiService.post(`/bi/interactive-menus/${menuId}/send`, {
      phone_number: phoneNumber,
      connection_id: connectionId,
    });
    return response;
  }

  /**
   * Clona configurações de um cliente para outro
   */
  async cloneSettings(
    sourceClientId: number,
    targetClientId: number,
    options: Array<'prompts' | 'structure' | 'menus'>
  ): Promise<{
    success: boolean;
    summary: string;
    details?: {
      prompts?: number;
      structure?: { departments: number; agents: number };
      menus?: number;
    };
  }> {
    const response = await apiService.post('/bi/clone-settings', {
      source_client_id: sourceClientId,
      target_client_id: targetClientId,
      options,
    });
    return response;
  }
}

const biApi = new BIApi();
export default biApi;

